import {getEnv} from '@github-ui/client-env'
import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {CopilotAnimation, CopilotAnimationType} from '@github-ui/copilot-animation'
import {CopilotAuthTokenProvider} from '@github-ui/copilot-auth-token'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useMutation} from '@github-ui/react-query'
import {isStaff} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDynamicTextareaHeight} from '@github-ui/use-dynamic-textarea-height'
import {
  AlertIcon,
  CheckIcon,
  CopilotIcon,
  PaperAirplaneIcon,
  SquareFillIcon,
  SyncIcon,
  XIcon,
} from '@primer/octicons-react'
import {Button, Flash, IconButton, Label, Textarea, useConfirm} from '@primer/react'
import {clsx} from 'clsx'
import type {RefObject} from 'react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {LABELS} from './constants/labels'
import styles from './CopilotGenerateDescriptionPanel.module.css'

const SYSTEM_PROMPT = `You are a helpful assistant that helps users create and refine GitHub issue titles and descriptions.
Your task is to take the user's input and the current issue title and body, and create or improve them.

For the issue body:

- Provide a clear, comprehensive issue body in markdown format that explains the problem, context, and any relevant details. Keep it clear and concise.
- If there is an existing body that has a structure (template), work with the template. DO NOT change the template unless instructed.
- Return the body in a markdown format, unwrapped by \`\`\`markdown tags.
- Only work with information you have. The length of the issue should be proportional to the information provided.
- Try to focus updates on the portions of the body that are relevant to the user's request.
- When the user should be directed to edit portions of the returned body, explicitly put areas to edit [inside brackets with instructions]. Do not add meta instructions outside the main body content.

For the issue title:
- Only update the title if it reflects incorrect or incomplete information based on the user's request.

Return your response as a JSON object with "title" and "body" fields.`

export type CopilotGenerateDescriptionPanelProps = {
  isOpen: boolean
  onClose: () => void
  onGenerate: (title: string, description: string) => void
  repoOwner: string
  repoName: string
  title: string
  body: string
  className?: string
  onPendingChange?: (isPending: boolean) => void
  /** Callback when panel state changes (initial, loading, suggested, error) */
  onStateChange?: (state: PanelState) => void
  /** Controls whether the panel anchors to the top or bottom of the editor */
  anchorPosition?: 'top' | 'bottom'
  /** Callback when panel height changes, for adjusting textarea padding */
  onHeightChange?: (height: number) => void
  /** Reference to the editor, used to position the panel relative to the input */
  editorRef?: RefObject<CommentBoxHandle | null>
}

type CopilotResponse = {
  title: string
  body: string
}

export type PanelState = 'initial' | 'loading' | 'suggested' | 'error'

export function CopilotGenerateDescriptionPanel({
  isOpen,
  onClose,
  onGenerate,
  repoOwner,
  repoName,
  title,
  body,
  className,
  onPendingChange,
  onStateChange,
  anchorPosition = 'top',
  onHeightChange,
  editorRef,
}: CopilotGenerateDescriptionPanelProps) {
  const [userInput, setUserInput] = useState('')
  const [panelState, setPanelState] = useState<PanelState>('initial')
  const [lastUserMessage, setLastUserMessage] = useState('')

  // Keep track of the initial title and body in case we must revert back to them
  const initialTitleRef = useRef(title)
  const initialBodyRef = useRef(body)
  const prevIsOpenRef = useRef(isOpen)

  // Keep track of the last sent title/body to allow retrying from the same state with "Try again"
  const lastSentTitleRef = useRef(title)
  const lastSentBodyRef = useRef(body)

  // Keep track of the last generated title/body to detect user edits
  const generatedTitleRef = useRef<string | null>(null)
  const generatedBodyRef = useRef<string | null>(null)

  const authTokenProviderRef = useRef(new CopilotAuthTokenProvider([]))
  const confirm = useConfirm()
  const abortControllerRef = useRef<AbortController | null>(null)
  const {sendAnalyticsEvent} = useAnalytics()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const userIsStaff = useMemo(() => isStaff(), [])

  // Dynamic textarea height: starts at 1 row, expands up to 10 rows
  const heightStyles = useDynamicTextareaHeight({
    minHeightLines: 1,
    maxHeightLines: 10,
    elementRef: textareaRef,
    value: userInput,
  })

  // Track the calculated top position for the panel based on anchor position
  const [positionOffset, setPositionOffset] = useState<number | undefined>(undefined)

  // Calculate and update position offsets based on the editor's input position
  useLayoutEffect(() => {
    if (!isOpen) return

    const inputElement = editorRef?.current?.inputRef.current
    if (!inputElement) return

    const calculateOffset = () => {
      // Use the input's bounding rect to calculate offset from document top
      // Then subtract the panel container's offset to get relative position
      const panelContainer = panelRef.current?.offsetParent as HTMLElement | null
      if (!panelContainer) return

      const inputRect = inputElement.getBoundingClientRect()
      const containerRect = panelContainer.getBoundingClientRect()

      if (anchorPosition === 'top') {
        // The offset is where the input starts relative to the panel's positioned ancestor
        setPositionOffset(inputRect.top - containerRect.top)
      } else {
        // Calculate bottom position as a top value
        // This positions the panel's top edge at the input's bottom edge (excluding padding)
        const inputStyles = getComputedStyle(inputElement)
        const inputPaddingBottom = parseFloat(inputStyles.paddingBottom) || 0
        setPositionOffset(inputRect.bottom - containerRect.top - inputPaddingBottom)
      }
    }

    // Set initial offset
    calculateOffset()

    // Create ResizeObserver to update offset when layout changes
    const resizeObserver = new ResizeObserver(() => {
      calculateOffset()
    })

    // Observe the input's parent for layout changes
    if (inputElement.parentElement) {
      resizeObserver.observe(inputElement.parentElement)
    }
    // Also observe the panel itself to recalculate when its height changes
    if (panelRef.current) {
      resizeObserver.observe(panelRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [anchorPosition, editorRef, isOpen])

  // Update initial refs when panel transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      initialTitleRef.current = title
      initialBodyRef.current = body
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, title, body])

  // Notify parent when panel state changes
  useEffect(() => {
    onStateChange?.(panelState)
  }, [panelState, onStateChange])

  // Derive current message and action button visibility from panel state
  const currentMessage = useMemo(() => {
    switch (panelState) {
      case 'initial':
        return LABELS.copilotPanelInitialMessage
      case 'suggested':
        return LABELS.copilotPanelUpdatedMessage
      case 'error':
        return LABELS.copilotGenerateError
      case 'loading':
        return ''
      default:
        return ''
    }
  }, [panelState])

  const {mutate, isPending} = useMutation<CopilotResponse, Error, string>({
    mutationFn: async (userMessage: string) => {
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController()

      const apiUrl = `${getEnv().copilotApiOverrideUrl || 'https://api.githubcopilot.com'}/chat/completions`
      const token = await authTokenProviderRef.current.getAuthToken()
      const headers: {[key: string]: string} = {
        Authorization: token.authorizationHeaderValue,
        'copilot-integration-id': 'copilot-embedded-experience',
        'Content-Type': 'application/json',
      }

      // Always use current working state (title/body from form) + user's new request
      const userContext = `Current title: ${title || '(empty)'}
Current body: ${body || '(empty)'}

User's request: ${userMessage}`

      // Validate input length to prevent API failures
      const MAX_COMBINED_LENGTH = 50000
      if (userContext.length > MAX_COMBINED_LENGTH) {
        throw new Error('Input is too large. Please reduce the length of your title, description, or request.')
      }

      const apiMessages = [
        {role: 'system', content: SYSTEM_PROMPT},
        {role: 'user', content: userContext},
      ]

      const requestBody = {
        messages: apiMessages,
        model: 'gpt-4o-mini',
        stream: false,
        temperature: 0.3,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'issue_content',
            strict: true,
            schema: {
              type: 'object',
              properties: {
                title: {
                  type: 'string',
                  description: 'The improved or generated issue title',
                },
                body: {
                  type: 'string',
                  description: 'The improved or generated issue body/description',
                },
              },
              required: ['title', 'body'],
              additionalProperties: false,
            },
          },
        },
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      })

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`)
      }

      const data = await response.json()
      const content = data.choices?.[0]?.message?.content

      if (!content) {
        throw new Error('No content in response')
      }

      const parsed = JSON.parse(content) as CopilotResponse

      if (!parsed.body) {
        throw new Error('Invalid response structure')
      }

      return parsed
    },
    onSuccess: (data, _userMessage) => {
      // Transition to suggested state
      setPanelState('suggested')

      // Track what Copilot generated so we can detect user edits
      generatedTitleRef.current = data.title
      generatedBodyRef.current = data.body

      // Update the issue form (workingTitle/workingBody)
      onGenerate(data.title, data.body)

      // Clear input
      setUserInput('')

      sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_GENERATE_DESCRIPTION_WITH_COPILOT_PANEL', {
        repoNWO: `${repoOwner}/${repoName}`,
        hasTitle: !!title,
        hasExistingDescription: !!body,
        success: true,
      })

      sendGenerateDescriptionEvent()
    },
    onError: (error, _userMessage) => {
      // Handle user cancellation - don't show error, just reset to initial state
      if (error.name === 'AbortError') {
        setPanelState('initial')
        return
      }

      // Transition to error state
      setPanelState('error')

      sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_GENERATE_DESCRIPTION_WITH_COPILOT_PANEL', {
        repoNWO: `${repoOwner}/${repoName}`,
        hasTitle: !!title,
        hasExistingDescription: !!body,
        success: false,
        error: error.message,
      })

      sendGenerateDescriptionEvent(error.message)
    },
  })

  const handleClose = useCallback(() => {
    setUserInput('')
    setPanelState('initial')
    setLastUserMessage('')
    // Reset the lastSent refs to current values when closing
    lastSentTitleRef.current = title
    lastSentBodyRef.current = body
    // Reset generated refs
    generatedTitleRef.current = null
    generatedBodyRef.current = null
    onClose()
  }, [onClose, title, body])

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort()
    // Note: panelState will be reset to 'initial' in onError handler when AbortError is caught
    // User input is preserved intentionally
  }, [])

  const handleSend = useCallback(() => {
    if (!userInput.trim() || isPending) return

    // Save the current title/body before making the request
    lastSentTitleRef.current = title
    lastSentBodyRef.current = body
    setLastUserMessage(userInput)
    // Transition to loading state
    setPanelState('loading')
    mutate(userInput)
  }, [userInput, isPending, mutate, title, body])

  const handleDiscard = useCallback(async () => {
    // Check if user has edited the generated content
    const hasUserEdits =
      generatedTitleRef.current !== null &&
      generatedBodyRef.current !== null &&
      (title !== generatedTitleRef.current || body !== generatedBodyRef.current)

    if (hasUserEdits) {
      const confirmed = await confirm({
        title: LABELS.copilotDiscardConfirmDialog.title,
        content: LABELS.copilotDiscardConfirmDialog.content,
        confirmButtonContent: LABELS.copilotDiscardConfirmDialog.confirm,
        confirmButtonType: 'danger',
        cancelButtonContent: LABELS.copilotDiscardConfirmDialog.cancel,
      })

      if (!confirmed) {
        return
      }
    }

    // Revert to initial state captured when component first mounted
    onGenerate(initialTitleRef.current, initialBodyRef.current)
    handleClose()
  }, [onGenerate, handleClose, title, body, confirm])

  const handleTryAgain = useCallback(async () => {
    if (!lastUserMessage) return

    // Check if user has edited the generated content
    const hasUserEdits =
      generatedTitleRef.current !== null &&
      generatedBodyRef.current !== null &&
      (title !== generatedTitleRef.current || body !== generatedBodyRef.current)

    if (hasUserEdits) {
      const confirmed = await confirm({
        title: LABELS.copilotTryAgainConfirmDialog.title,
        content: LABELS.copilotTryAgainConfirmDialog.content,
        confirmButtonContent: LABELS.copilotTryAgainConfirmDialog.confirm,
        confirmButtonType: 'danger',
        cancelButtonContent: LABELS.copilotTryAgainConfirmDialog.cancel,
      })

      if (!confirmed) {
        return
      }
    }

    // Revert to the title/body from before the last attempt, then resubmit
    onGenerate(lastSentTitleRef.current, lastSentBodyRef.current)
    // Transition to loading state
    setPanelState('loading')
    // Resubmit the last user message
    mutate(lastUserMessage)
  }, [lastUserMessage, mutate, onGenerate, title, body, confirm])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  // Focus the textarea when panel opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [isOpen])

  // Notify parent when pending state changes
  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  // Cleanup on unmount: notify parent that pending state is false, though this is an edge case
  // since the panel cannot be closed while there is a pending request
  useEffect(() => {
    return () => {
      onPendingChange?.(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      // Abort any in-flight request when component unmounts
      abortControllerRef.current?.abort()
    }
  }, [])

  // Track panel height changes for dynamic textarea padding
  // Using useLayoutEffect to report height synchronously before paint,
  // preventing the "pop-in" effect when the panel is anchored to the bottom
  useLayoutEffect(() => {
    if (!isOpen || !panelRef.current || !onHeightChange) return

    // Report initial height synchronously to prevent visual jump
    const initialHeight = panelRef.current.getBoundingClientRect().height
    onHeightChange(initialHeight + 16) // Add padding for margins

    // Continue observing for dynamic height changes
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        // Use borderBoxSize to match getBoundingClientRect (includes padding + border)
        // Fall back to getBoundingClientRect if borderBoxSize is not available
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height
        onHeightChange(height + 16) // Add padding for margins
      }
    })

    observer.observe(panelRef.current)

    return () => {
      observer.disconnect()
      onHeightChange(0)
    }
  }, [isOpen, onHeightChange])

  if (!isOpen) return null

  return (
    <div
      ref={panelRef}
      className={clsx(
        styles.container,
        anchorPosition === 'top' ? styles.anchorTop : anchorPosition === 'bottom' ? styles.anchorBottom : undefined,
        className,
      )}
      style={positionOffset !== undefined ? {top: `${positionOffset}px`} : undefined}
      role="region"
      aria-label="Generate with Copilot"
    >
      {!isPending && (
        <div className={styles.closeButtonContainer}>
          {userIsStaff && (
            <a
              href="https://gh.io/write-with-copilot-feedback"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.staffPillLink}
            >
              <Label variant="accent">Staff</Label>
            </a>
          )}
          <IconButton aria-label="Close" icon={XIcon} variant="invisible" onClick={handleClose} />
        </div>
      )}

      {/* Messages */}
      {currentMessage && (
        <div className={styles.messageRow}>
          <div className={styles.iconContainer}>
            <CopilotIcon size={16} />
          </div>
          <div className={styles.messageContent}>
            <div className={styles.messageLabel}>
              <span>Copilot</span>
            </div>
            {currentMessage === LABELS.copilotGenerateError ? (
              <div className={styles.errorContainer}>
                <Flash variant="danger">
                  <AlertIcon />
                  {currentMessage}
                </Flash>
              </div>
            ) : (
              <div className={styles.messageText}>
                <span>{currentMessage}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {isPending && (
        <div className={styles.loadingContainer} aria-live="polite" aria-atomic="true" role="status">
          <CopilotAnimation animationType={CopilotAnimationType.Thinking} loopAnimation size={20} />
          <span className={styles.loadingLabel}>Copilot</span>
          <WithShimmerEffect className={styles.loadingText}>{LABELS.copilotPanelLoadingMessage}</WithShimmerEffect>
          <IconButton aria-label="Stop generation" icon={SquareFillIcon} size="small" onClick={handleCancel} />
        </div>
      )}

      {/* Action buttons - shown after any successful generation */}
      {panelState === 'suggested' && !isPending && (
        <div className={styles.actionButtons}>
          <Button
            size="small"
            variant="invisible"
            className={styles.actionButton}
            leadingVisual={CheckIcon}
            onClick={handleClose}
          >
            {LABELS.copilotPanelAcceptButton}
          </Button>
          <Button
            size="small"
            variant="invisible"
            className={styles.actionButton}
            leadingVisual={XIcon}
            onClick={handleDiscard}
          >
            {LABELS.copilotPanelDiscardButton}
          </Button>
          <Button
            size="small"
            variant="invisible"
            className={styles.actionButton}
            leadingVisual={SyncIcon}
            onClick={handleTryAgain}
          >
            {LABELS.copilotPanelTryAgainButton}
          </Button>
        </div>
      )}

      {/* Input area - hidden during loading */}
      {!isPending && (
        <div className={styles.inputArea}>
          <Textarea
            ref={textareaRef}
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={LABELS.copilotPanelInputPlaceholder}
            rows={1}
            resize="none"
            block
            className={styles.textarea}
            style={heightStyles}
          />
          <IconButton
            className={styles.sendButton}
            aria-label={LABELS.copilotPanelSendButton}
            icon={PaperAirplaneIcon}
            variant="invisible"
            onClick={handleSend}
            disabled={userInput.trim() === ''}
          />
        </div>
      )}
    </div>
  )
}

function sendGenerateDescriptionEvent(error?: string | null) {
  sendEvent('copilot.create_issue_description_panel', {
    error,
  })
}

try{ CopilotGenerateDescriptionPanel.displayName ||= 'CopilotGenerateDescriptionPanel' } catch {}